/**
 * Shared helpers for reasoning about a fitted Weibull curve, used by both the configuration-tab
 * preview (survival probability, from the infer endpoint) and the results tab (failure
 * probability, from a completed run).
 */

/**
 * Fitted Weibull coefficients as the API returns them: `alpha` is the scale (η), `beta` the shape
 * (β) and, on 3-parameter fits only, `gamma` the location (c). Left as an open record because the
 * API owns the key set and may report coefficients we do not display.
 */
export type WeibullParams = Record<string, number>;

/**
 * Widest spread, in percentage points, that still counts as "this curve does not move".
 *
 * Both callers pass probabilities already scaled to 0–100, so this is a hundredth of a percentage
 * point: below it a curve is indistinguishable at any precision the UI renders, and it cannot
 * approach a failure threshold within any age worth modelling.
 *
 * A tolerance rather than exact equality because a diverged fit does not have to underflow all the
 * way to be useless. η=5000, β=10 over ages 0–150 yields values differing only in the 16th
 * significant digit — flat to the eye, but not bit-identical, so strict equality missed it.
 */
const FLAT_SERIES_TOLERANCE = 0.01;

/**
 * A curve is unusable when it never moves: effectively the same probability at every age means the
 * model can never cross a failure threshold and no remaining life can be estimated. Two points are
 * the minimum needed to establish it — a single point says nothing about change over time.
 *
 * Shape-agnostic so it can be applied to survival probability on the preview and failure
 * probability on a run, which arrive in different point shapes. Values are expected on a 0–100
 * scale; see `FLAT_SERIES_TOLERANCE`.
 *
 * Any missing or non-finite value makes the question unanswerable, so such a series is not flat:
 * reporting "this curve is flat" for a response that simply had no data would be a false alarm.
 *
 * This catches a diverged fit that the API still reports as successful. See I360A-2863, where a
 * 3-parameter fit returned η≈58,362 and β≈17,800, making `((t-c)/η)^β` underflow to 0 and every
 * survival probability exactly 1.
 */
export const isFlatSeries = (values: (number | null | undefined)[]): boolean => {
	if (values.length < 2) return false;

	let min = Number.POSITIVE_INFINITY;
	let max = Number.NEGATIVE_INFINITY;
	for (const value of values) {
		if (typeof value !== 'number' || !Number.isFinite(value)) return false;
		if (value < min) min = value;
		if (value > max) max = value;
	}

	return max - min < FLAT_SERIES_TOLERANCE;
};

/**
 * Renders the fitted coefficients as a single line, or null when there is nothing to report.
 * Two decimals throughout: these are diagnostic, and a diverged fit is recognisable from its
 * magnitude rather than its precision. `gamma` is present on 3-parameter fits only.
 */
// Shape before scale before location, which is how the formulation is written: S(t) = exp(-((t-c)/η)^β).
// Any coefficient the API reports that is not in this list is not displayed.
const DISPLAYED_COEFFICIENTS: readonly (readonly [key: string, symbol: string])[] = [
	['beta', 'β'],
	['alpha', 'η'],
	['gamma', 'c'],
];

export const describeCoefficients = (params?: WeibullParams): string | null => {
	if (!params) return null;

	const parts = DISPLAYED_COEFFICIENTS.filter(([key]) => params[key] !== undefined).map(
		([key, symbol]) => `${symbol} ${params[key].toFixed(2)}`,
	);

	return parts.length > 0 ? parts.join('  ·  ') : null;
};

/**
 * The age a 3-parameter fit is comparable at. The location parameter absorbs most of the
 * characteristic age, so `alpha` on its own reads far smaller than a 2-parameter `alpha` for the
 * same data — `gamma + alpha` is the quantity that matches it. Null for 2-parameter fits, where
 * there is no location and the characteristic age is just `alpha`.
 */
export const characteristicAge = (params?: WeibullParams): number | null => {
	if (params?.gamma === undefined || params.alpha === undefined) return null;
	return params.gamma + params.alpha;
};
